import React, { useCallback, useMemo, useRef } from 'react'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { BadgeSecondary, BadgeSuccess } from '../../../../../shared/component/badge'
import { Menu, MenuAdd, MenuExport, MenuItem, MenuRecycle, MenuTableSettingToggle } from '../../../../../shared/menu/menu'
import { CardFullHeightScrollY } from '../../../../../shared/card/card-full-height-scroll-y'
import { IconActivityLog, IconAdd, IconUsers } from '../../../../../shared/icon/icon'
import { useAuditCapabilities } from '../../../../../shared/audit/use-audit-capabilities'
import { useApiListUserLogin } from '../../../../../api-new/state/content'
import { useRoleOrgUser } from '../../../../../role/hook/role-org'
import { useUserMenuItems } from './common/user-menu-items'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ColumnDateTime, ColumnMenu, ColumnSelector, ColumnText } from '../../../../../shared/table-builder/table-columns'
import moment from 'moment'
import { IconSelectListReadonly } from '../../../../../shared/icon/icon'
import { managedLabel } from './common/managed-by'

const BadgeStatus = ({ userLogin }) => {
  let value = useMemo(
    () => {
      if (userLogin.inviteStatus === 'expired') return <BadgeSecondary value="Invite expired" />
      if (userLogin.inviteStatus === 'revoked') return <BadgeSecondary value="Invite revoked" />
      if (userLogin.inviteStatus === 'declined') return <BadgeSecondary value="Invite declined" />
      if (userLogin.invitePending) return <BadgeSecondary value="Invited" />
      else return <BadgeSuccess value="Registered" />
    },
    [userLogin]
  )

  return (
    <React.Fragment>
      {value}
    </React.Fragment>
  )
}

var second = 1000
var minute = second * 60
var hour = minute * 60
var day = hour * 24
var day7 = day * 7
var month = day * 30
var month6 = 6 * month
var year = month * 12

const lastActive = userLogin => {
  if (userLogin.invitePending) {
    return 'Never'
  }

  if (userLogin.lastActive) {
    let now = moment()
    let auth = moment(userLogin.lastActive)
    let diff = now.diff(auth)

    if (diff > year) {
      return 'Over a Year'
    } else if (diff > month6) {
      return 'Past Year'
    } else if (diff > month) {
      return 'Past 6 Months'
    } else if (diff > day7) {
      return 'Past Month'
    } else if (diff > day) {
      return 'Past Week'
    } else {
      return 'Today'
    }
  }

  return 'Never'
}

const ManagedCell = ({ managedBy }) => {
  const text = managedLabel(managedBy)
  return text ? <span title={text}>{managedBy.kind === 'scim' && <IconSelectListReadonly className="me-1" />}{text}</span> : null
}

const comparatorManaged = (a, b) => managedLabel(a).localeCompare(managedLabel(b))

export const UserLoginList = () => {
  const apiListUserLogin = useApiListUserLogin()

  const roleOrgUser = useRoleOrgUser()
  const caps = useAuditCapabilities()
  // the same items as the user page's menu, plus Edit
  const userMenuItems = useUserMenuItems()

  const handleReady = async () => await apiListUserLogin.get()
  const handleDestroyed = () => apiListUserLogin.init()

  const tableRef = useRef()
  const handleExportCsv = () => tableRef.current.exportCsv('users')
  const handleTableSettingToggle = () => tableRef.current.toggleSideBar()

  const HeaderMenu = () =>
    <Menu>
      <MenuAdd text="add users" to="/org/user/list/add" show={roleOrgUser.accessCreate} />
      <MenuExport onClick={handleExportCsv} show={roleOrgUser.accessRead} />
      <MenuItem Icon={IconActivityLog} text="audit log" to="/org/audit-log?type=users|logins" show={caps.canAudit} />
      <MenuRecycle to="/org/user/list/recycle" show={roleOrgUser.accessDelete} />
      <MenuTableSettingToggle onClick={handleTableSettingToggle} show />
    </Menu>

  const comparatorStatus = useCallback((value1, value2, node1, node2) => {
    if (!node1.data.invitePending && !node2.data.invitePending) {
      return -1
    }

    if (!node1.data.lastActive && !node2.data.lastActive) {
      return -1
    }

    let date1 = new Date(node1.data.lastActive)
    let date2 = new Date(node2.data.lastActive)

    if (date1 > date2) {
      return 1
    } else {
      return -1
    }
  }, [])

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconUsers} text="Users">
        <CardFullHeightScrollY title="Users" HeaderMenu={HeaderMenu}>
          <TableAgClient
            ref={tableRef}
            apiList={apiListUserLogin}
            onReady={handleReady}
            onDestoryed={handleDestroyed}
            emptyState={{
              Icon: IconUsers,
              title: 'No users yet',
              message: 'People you add to your organization appear here.',
              action: { label: 'Add users', Icon: IconAdd, to: '/org/user/list/add', show: roleOrgUser.accessCreate }
            }}>
            <ColumnDateTime field="createdAt" hide />
            <ColumnText field="email" />
            <ColumnText field="firstName" />
            <ColumnText field="lastName" />
            <ColumnSelector field="managedBy" header="Managed by" selector={row => <ManagedCell managedBy={row.managedBy} />} comparator={comparatorManaged} />
            <ColumnSelector field="lastActive" header="Status" selector={row => <BadgeStatus userLogin={row} />} comparator={comparatorStatus} />
            <ColumnSelector field="lastActive" header="Last Active" selector={row => lastActive(row)} />
            <ColumnMenu menu={row => <Menu small>{userMenuItems(row.userID, { edit: true })}</Menu>} />
          </TableAgClient>
        </CardFullHeightScrollY>
      </BreadcrumbView>
    </React.Fragment>
  )
}
